import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment/moment";
import axios from "axios";
import {
  BsPrinter,
  BsDownload,
  BsShare,
  BsThreeDotsVertical,
  BsPersonCircle,
  BsCashCoin,
  BsCreditCard2Back,
  BsTag,
  BsBagCheck,
  BsArrowRepeat,
  BsClockHistory,
  BsTelephone,
  BsEnvelope,
  BsGlobe,
  BsBuilding,
} from "react-icons/bs";
import { MdFlight as BsAirplane } from "react-icons/md";
import { get_Order_detail_v2 } from "../../../services/authServices";
import { TICKES_DOWNLOAD, INVOICE_DOWNLOAD } from "../../../constant/api";
import "./orderDetailsV2.css";

/**
 * The upstream data isn't reliably shaped (see docs/FLIGHT_ORDER_ADMIN_V2_BLUEPRINT.md) —
 * some fields the schema declares as String have shown up in Mongo as objects. Never hand
 * React a raw object as a child — stringify it instead of crashing the page.
 */
const renderValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (React.isValidElement(value)) return value;
  if (typeof value === "object") return JSON.stringify(value);
  return value;
};

const money = (value) => (value == null ? "-" : `₹${Number(value).toLocaleString("en-IN")}`);
const dateTime = (value) => (value ? moment(value).format("DD MMM YYYY, hh:mm A") : "-");
const dateOnly = (value) => (value ? moment(value).format("DD MMM YYYY") : "-");
const hoursMinutes = (mins) => {
  if (mins == null) return "-";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
};
const initialsOf = (passenger) => {
  const initials = `${passenger?.firstName?.[0] ?? ""}${passenger?.lastName?.[0] ?? ""}`.toUpperCase();
  return initials || "?";
};
const paxSelectionSummary = (items) => {
  if (!items || items.length === 0) return "None";
  const labels = items.map((item) => item?.desc || item?.code).filter(Boolean);
  return labels.length ? labels.join(", ") : "None";
};

function downloadBlob(data, filename, type) {
  const blob = new Blob([data], { type });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

function bookingStatusVariant(status) {
  if (status === "SUCCESS") return "success";
  if (["CANCELLED", "ABORTED", "FAILED"].includes(status)) return "danger";
  if (status) return "warning";
  return "muted";
}
function paymentStatusVariant(status) {
  if (/success/i.test(status ?? "")) return "success";
  if (/fail/i.test(status ?? "")) return "danger";
  if (status) return "warning";
  return "muted";
}

const Badge = ({ variant = "neutral", children }) => (
  <span className={`odv2-badge odv2-badge--${variant}`}>{renderValue(children)}</span>
);

const Card = ({ icon, title, children }) => (
  <div className="odv2-card">
    <div className="odv2-card-header">
      {icon} {title}
    </div>
    <div className="odv2-card-body">{children}</div>
  </div>
);

const InfoItem = ({ label, value }) => (
  <div className="odv2-info-item">
    <label>{label}</label>
    <div>{renderValue(value)}</div>
  </div>
);

const PolicyBlock = ({ title, policy }) => {
  if (!policy) return null;
  return (
    <div className="odv2-policy">
      <div>
        <b>{title}</b>
        {policy.totalFee > 0 && <span> — Fee: {money(policy.totalFee)}</span>}
      </div>
      {policy.description && <p>{renderValue(policy.description)}</p>}
    </div>
  );
};

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "travellers", label: "Travellers" },
  { key: "journey", label: "Journey" },
  { key: "fare", label: "Fare & Charges" },
  { key: "payments", label: "Payments" },
  { key: "offers", label: "Offers" },
  { key: "services", label: "Additional Services" },
  { key: "refund", label: "Refund & Cancellation" },
  { key: "timeline", label: "Timeline" },
];

const OrderDetailsV2 = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (!id) return;
    const fetchDetail = async () => {
      setIsLoading(true);
      setNotFound(false);
      try {
        const res = await get_Order_detail_v2(id);
        if (res?.data?.Status === 404) {
          setNotFound(true);
          setOrder(null);
        } else {
          setOrder(res?.data?.Data ?? null);
        }
      } catch (e) {
        console.error(e);
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetail();
  }, [id]);

  const handlePrintTicket = async () => {
    try {
      const response = await axios.post(TICKES_DOWNLOAD, { orderId: order.orderId }, { responseType: "blob" });
      downloadBlob(response.data, `${order.orderId}-ticket.pdf`, "application/pdf");
    } catch (e) {
      console.error(e);
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      const response = await axios.post(INVOICE_DOWNLOAD, { orderId: order.orderId }, { responseType: "blob" });
      downloadBlob(response.data, `${order.orderId}-invoice.pdf`, "application/pdf");
    } catch (e) {
      console.error(e);
    }
  };

  if (isLoading) return <div className="odv2-empty-state">Loading…</div>;

  if (notFound || !order) {
    return (
      <div className="odv2-empty-state">
        <p>Order not found.</p>
        <button className="odv2-btn" onClick={() => navigate("/flights")}>
          Back to list
        </button>
      </div>
    );
  }

  const uniquePnrs = [...new Set((order.pnr ?? []).map((p) => p.pnr).filter(Boolean))];
  const pnrDisplay = uniquePnrs.length ? uniquePnrs.join(", ") : "N/A";
  const confirmedLabel =
    order.bookingStatusLabel === "CONFIRMED" ? "Booking Confirmed" : order.bookingStatusLabel ?? order.bookingStatus;

  const allServices = (order.passengers ?? []).flatMap((passenger) => {
    const name = `${passenger.title ?? ""} ${passenger.firstName ?? ""} ${passenger.lastName ?? ""}`.trim();
    const rows = [];
    for (const item of passenger.selectedBaggage ?? []) {
      rows.push({ name, type: "Baggage", desc: item?.desc ?? item?.code, amount: item?.amount });
    }
    for (const item of passenger.selectedMeal ?? []) {
      rows.push({ name, type: "Meal", desc: item?.desc ?? item?.code, amount: item?.amount });
    }
    for (const item of passenger.selectedSeat ?? []) {
      rows.push({ name, type: "Seat", desc: item?.desc ?? item?.seatNo ?? item?.code, amount: item?.amount });
    }
    return rows;
  });

  return (
    <div className="odv2-page">
      <div className="odv2-header">
        <div>
          <div className="odv2-title-row">
            <h1 className="odv2-order-title">{order.orderId}</h1>
            <Badge variant={bookingStatusVariant(order.bookingStatus)}>{order.bookingStatus}</Badge>
          </div>
          <div className="odv2-header-meta">
            <span>✓ {confirmedLabel}</span>
            <span>
              PNR: <strong>{pnrDisplay}</strong>
            </span>
            <span>Booked on {dateTime(order.bookingDate)}</span>
          </div>
        </div>
        <div className="odv2-header-actions">
          <button className="odv2-btn" onClick={handlePrintTicket}>
            <BsPrinter /> Print Ticket
          </button>
          <button className="odv2-btn" onClick={handleDownloadInvoice}>
            <BsDownload /> Download Invoice
          </button>
          <button className="odv2-btn" disabled title="Coming soon">
            <BsShare /> Share
          </button>
          <button className="odv2-btn odv2-btn--primary" disabled title="Coming soon">
            More Actions <BsThreeDotsVertical />
          </button>
        </div>
      </div>

      <div className="odv2-summary-row">
        <div className="odv2-summary-card">
          <div className="odv2-summary-head">
            <span className="odv2-summary-icon">
              <BsBuilding />
            </span>
            Order Information
          </div>
          <div className="odv2-summary-line">
            <span>TMT Order ID</span> <span>{order.orderId}</span>
          </div>
          <div className="odv2-summary-line">
            <span>Invoice</span> <span>{renderValue(order.orderInvoice)}</span>
          </div>
        </div>

        <div className="odv2-summary-card">
          <div className="odv2-summary-head">
            <span className="odv2-summary-icon">
              <BsCreditCard2Back />
            </span>
            Payment Status
          </div>
          <div className="odv2-summary-line">
            <span>Gateway</span> <span>{renderValue(order.payment?.gateway)}</span>
          </div>
          <div className="odv2-summary-line">
            <span>Paid Amount</span> <span>{money(order.payment?.paidAmount)}</span>
          </div>
          <Badge variant={paymentStatusVariant(order.payment?.status)}>{order.payment?.status ?? "-"}</Badge>
        </div>

        <div className="odv2-summary-card">
          <div className="odv2-summary-head">
            <span className="odv2-summary-icon">
              <BsBagCheck />
            </span>
            Booking Status
          </div>
          <div className="odv2-summary-line">
            <span>Booking Type</span> <span>{renderValue(order.bookingType)}</span>
          </div>
          <div className="odv2-summary-line">
            <span>Trip Type</span> <span>{renderValue(order.tripType)}</span>
          </div>
        </div>

        <div className="odv2-summary-card">
          <div className="odv2-summary-head">
            <span className="odv2-summary-icon">
              <BsAirplane />
            </span>
            Journey Summary
          </div>
          {order.journeySummary ? (
            <>
              <div className="odv2-summary-route">
                {order.journeySummary.waypoints.map((code, idx) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && <span>→</span>}
                    <span>{code}</span>
                  </React.Fragment>
                ))}
              </div>
              <div className="odv2-muted">
                {order.journeySummary.totalStops === 0 ? "Non Stop" : `${order.journeySummary.totalStops} Stop(s)`}
              </div>
              <div className="odv2-muted">
                {dateOnly(order.journeySummary.dateRange?.from)} – {dateOnly(order.journeySummary.dateRange?.to)}
              </div>
              <div className="odv2-muted">{order.journeySummary.totalSegments} Segment(s)</div>
            </>
          ) : (
            <span className="odv2-muted">-</span>
          )}
        </div>

        <div className="odv2-summary-card">
          <div className="odv2-summary-head">
            <span className="odv2-summary-icon">
              <BsCashCoin />
            </span>
            Fare Summary
          </div>
          <div className="odv2-summary-highlight">{money(order.fareSummary?.totalFare)}</div>
          <div className="odv2-summary-line">
            <span>Base Fare</span> <span>{money(order.fareSummary?.baseFare)}</span>
          </div>
          <div className="odv2-summary-line">
            <span>Taxes &amp; Fees</span> <span>{money(order.fareSummary?.taxAndFee)}</span>
          </div>
        </div>
      </div>

      <div className="odv2-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`odv2-tab ${activeTab === tab.key ? "odv2-tab--active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            {tab.key === "travellers" && ` (${order.passengers?.length ?? 0})`}
            {tab.key === "journey" && ` (${order.journeySummary?.totalSegments ?? 0})`}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <div className="odv2-content">
          <div>
            <Card icon={<BsPersonCircle />} title="Traveller Information">
              {(order.passengers ?? []).map((passenger, idx) => (
                <div className="odv2-passenger" key={idx}>
                  <div className="odv2-avatar">{initialsOf(passenger)}</div>
                  <div className="odv2-info-grid" style={{ flex: 1 }}>
                    <InfoItem
                      label={renderValue(passenger.paxType)}
                      value={`${passenger.title ?? ""} ${passenger.firstName ?? ""} ${passenger.lastName ?? ""}`}
                    />
                    <InfoItem label="Date of Birth" value={dateOnly(passenger.dob)} />
                    <InfoItem label="Passport" value={passenger.passport?.number ?? "NA"} />
                  </div>
                </div>
              ))}
            </Card>

            <Card icon={<BsAirplane />} title="Journey Information">
              {(order.itinerary ?? []).map((sector) => (
                <div className="odv2-sector" key={sector.sectorIndex}>
                  <div className="odv2-sector-label">
                    Segment {sector.sectorIndex + 1}
                    {sector.segments?.length > 1 && (
                      <span className="odv2-muted">({sector.segments.length} legs)</span>
                    )}
                  </div>
                  {(sector.segments ?? []).map((segment, idx) => (
                    <div className="odv2-segment-row" key={idx}>
                      <div>
                        <b>
                          {renderValue(segment.airlineName)} ({renderValue(segment.airlineCode)})
                        </b>
                        <div className="odv2-segment-sub">{renderValue(segment.equipment)}</div>
                      </div>
                      <div>
                        <div>{renderValue(segment.from?.code)}</div>
                        <div className="odv2-segment-sub">{dateTime(segment.departure)}</div>
                      </div>
                      <div>
                        <div>{renderValue(segment.to?.code)}</div>
                        <div className="odv2-segment-sub">{dateTime(segment.arrival)}</div>
                      </div>
                      <div>
                        <div>PNR: {pnrDisplay}</div>
                        <div className="odv2-segment-sub">Duration: {hoursMinutes(segment.durationMin)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </Card>
          </div>

          <div>
            <Card icon={<BsTelephone />} title="Customer Contact">
              <div className="odv2-info-grid">
                <InfoItem
                  label={
                    <>
                      <BsTelephone /> Mobile
                    </>
                  }
                  value={order.contact?.mobile}
                />
                <InfoItem
                  label={
                    <>
                      <BsEnvelope /> Email
                    </>
                  }
                  value={order.contact?.email}
                />
              </div>
            </Card>

            <Card icon={<BsBuilding />} title="Merchant Information">
              <div className="odv2-info-grid">
                <InfoItem label="Merchant ID" value={order.merchantId} />
                <InfoItem label="Merchant Order ID" value={order.merchantOrderId} />
                <InfoItem label="TMT User ID" value={order.userId} />
              </div>
            </Card>

            <Card icon={<BsCreditCard2Back />} title="Payment Information">
              <div className="odv2-info-grid">
                <InfoItem label="Payment Gateway" value={order.payment?.gateway} />
                <InfoItem label="Payment Method" value={order.payment?.method} />
                <InfoItem label="Payment ID" value={order.payment?.gatewayPaymentId} />
                <InfoItem
                  label="Payment Status"
                  value={<Badge variant={paymentStatusVariant(order.payment?.status)}>{order.payment?.status ?? "-"}</Badge>}
                />
              </div>
            </Card>
          </div>
        </div>
      )}

      {activeTab === "travellers" && (
        <Card icon={<BsPersonCircle />} title={`Travellers (${order.passengers?.length ?? 0})`}>
          {(order.passengers ?? []).map((passenger, idx) => (
            <div className="odv2-passenger" key={idx}>
              <div className="odv2-avatar">{initialsOf(passenger)}</div>
              <div className="odv2-info-grid" style={{ flex: 1 }}>
                <InfoItem
                  label="Name"
                  value={`${passenger.title ?? ""} ${passenger.firstName ?? ""} ${passenger.lastName ?? ""}`}
                />
                <InfoItem label="Passenger Type" value={passenger.paxType} />
                <InfoItem label="Date of Birth" value={dateOnly(passenger.dob)} />
                <InfoItem label="PAN" value={passenger.pan ?? "NA"} />
                <InfoItem label="Passport Number" value={passenger.passport?.number ?? "NA"} />
                <InfoItem label="Passport Nationality" value={passenger.passport?.nationality ?? "NA"} />
                <InfoItem label="Passport Issue Date" value={dateOnly(passenger.passport?.issueDate) ?? "NA"} />
                <InfoItem label="Passport Expiry Date" value={dateOnly(passenger.passport?.expiryDate) ?? "NA"} />
                <InfoItem label="Baggage" value={paxSelectionSummary(passenger.selectedBaggage)} />
                <InfoItem label="Meal" value={paxSelectionSummary(passenger.selectedMeal)} />
                <InfoItem label="Seat" value={paxSelectionSummary(passenger.selectedSeat)} />
              </div>
            </div>
          ))}
        </Card>
      )}

      {activeTab === "journey" && (
        <Card icon={<BsAirplane />} title="Journey Details">
          {(order.itinerary ?? []).map((sector) => (
            <div className="odv2-sector" key={sector.sectorIndex}>
              <div className="odv2-sector-label">
                {sector.sectorIndex === 0 ? "Onward" : "Return"} — Segment {sector.sectorIndex + 1}
              </div>
              {(sector.segments ?? []).map((segment, idx) => (
                <div className="odv2-segment-row" key={idx}>
                  <div>
                    <b>
                      {renderValue(segment.airlineName)} ({renderValue(segment.airlineCode)}){" "}
                      {renderValue(segment.flightNumber)}
                    </b>
                    <div className="odv2-segment-sub">{renderValue(segment.equipment)}</div>
                  </div>
                  <div>
                    <div>{renderValue(segment.from?.code)}</div>
                    <div className="odv2-segment-sub">{dateTime(segment.departure)}</div>
                    <div className="odv2-segment-sub">
                      {renderValue(segment.from?.name)}
                      {segment.from?.terminal ? `, ${segment.from.terminal}` : ""}
                    </div>
                  </div>
                  <div>
                    <div>{renderValue(segment.to?.code)}</div>
                    <div className="odv2-segment-sub">{dateTime(segment.arrival)}</div>
                    <div className="odv2-segment-sub">
                      {renderValue(segment.to?.name)}
                      {segment.to?.terminal ? `, ${segment.to.terminal}` : ""}
                    </div>
                  </div>
                  <div>
                    <div>Duration: {hoursMinutes(segment.durationMin)}</div>
                    {segment.isRescheduled && <Badge variant="warning">Rescheduled</Badge>}
                  </div>
                </div>
              ))}

              {sector.fareRules && (
                <div className="odv2-info-grid" style={{ marginTop: 8 }}>
                  <InfoItem label="Fare Basis" value={sector.fareRules.fareBasisCode} />
                  <InfoItem label="Booking Class" value={sector.fareRules.bookingClass} />
                  {Object.entries(sector.fareRules.baggageAllowance ?? {}).map(([paxType, allowance]) => (
                    <InfoItem
                      key={paxType}
                      label={`${paxType} Baggage`}
                      value={`Check-in ${allowance.checkIn ?? "-"}, Cabin ${allowance.cabin ?? "-"}`}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </Card>
      )}

      {activeTab === "fare" && (
        <Card icon={<BsCashCoin />} title="Fare & Charges">
          <div className="odv2-info-grid">
            <InfoItem label="Base Fare" value={money(order.fareSummary?.baseFare)} />
            <InfoItem label="Net Fare" value={money(order.fareSummary?.netFare)} />
            <InfoItem label="Tax & Fee" value={money(order.fareSummary?.taxAndFee)} />
            <InfoItem label="Total Fare" value={money(order.fareSummary?.totalFare)} />
          </div>
          <hr />
          <div className="odv2-info-grid">
            {Object.entries(order.fareSummary?.taxBreakup ?? {})
              .filter(([, value]) => value != null)
              .map(([key, value]) => (
                <InfoItem key={key} label={key} value={money(value)} />
              ))}
          </div>
          <hr />
          <div className="odv2-info-grid">
            <InfoItem label="Sales Amount" value={money(order.commercials?.salesAmount)} />
            <InfoItem label="Convenience Fee" value={money(order.commercials?.convenienceFee)} />
            <InfoItem label="Net Margin" value={money(order.commercials?.netMargin)} />
            <InfoItem label="Output GST" value={money(order.commercials?.outputGST)} />
            <InfoItem label="Net Commission" value={money(order.commercials?.netCommission)} />
            <InfoItem label="Gross Commission" value={money(order.commercials?.grossCommission)} />
            <InfoItem label="TDS" value={money(order.commercials?.tds)} />
          </div>
        </Card>
      )}

      {activeTab === "payments" && (
        <Card icon={<BsCreditCard2Back />} title="Payment Details">
          <div className="odv2-info-grid">
            <InfoItem label="Gateway" value={order.payment?.gateway} />
            <InfoItem label="Method" value={order.payment?.method} />
            <InfoItem
              label="Status"
              value={<Badge variant={paymentStatusVariant(order.payment?.status)}>{order.payment?.status ?? "-"}</Badge>}
            />
            <InfoItem label="Paid Amount" value={money(order.payment?.paidAmount)} />
            <InfoItem label="Gateway Order ID" value={order.payment?.gatewayOrderId} />
            <InfoItem label="Gateway Payment ID" value={order.payment?.gatewayPaymentId} />
            <InfoItem label="Receipt" value={order.payment?.receipt} />
          </div>
          <hr />
          <div className="odv2-info-grid">
            <InfoItem label="PG % Charge" value={order.payment?.charges?.percentCharge ?? "-"} />
            <InfoItem label="PG Amount Charge" value={money(order.payment?.charges?.amountCharge)} />
            <InfoItem label="PG GST" value={money(order.payment?.charges?.gst)} />
            <InfoItem label="PG Net Received" value={money(order.payment?.charges?.netReceive)} />
          </div>
        </Card>
      )}

      {activeTab === "offers" && (
        <Card icon={<BsTag />} title="Offers Applied">
          {order.commercials?.offer ? (
            <div className="odv2-info-grid">
              <InfoItem label="Offer Code" value={order.commercials.offer.code} />
              <InfoItem label="Amount Off" value={money(order.commercials.offer.amountOff)} />
              <InfoItem label="Total Savings" value={money(order.commercials.offer.youSave ?? order.commercials.offer.amountOff)} />
            </div>
          ) : (
            <div className="odv2-empty-state">No offer applied on this order.</div>
          )}
        </Card>
      )}

      {activeTab === "services" && (
        <Card icon={<BsBagCheck />} title="Additional Services">
          {allServices.length === 0 ? (
            <div className="odv2-empty-state">No additional baggage, meal, or seat selections.</div>
          ) : (
            <table className="odv2-table">
              <thead>
                <tr>
                  <th>Passenger</th>
                  <th>Service</th>
                  <th>Description</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {allServices.map((row, idx) => (
                  <tr key={idx}>
                    <td>{renderValue(row.name)}</td>
                    <td>{row.type}</td>
                    <td>{renderValue(row.desc)}</td>
                    <td>{money(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {activeTab === "refund" && (
        <>
          <Card icon={<BsArrowRepeat />} title="Refund Status">
            {order.refund ? (
              <div className="odv2-info-grid">
                <InfoItem label="Status" value={order.refund.status} />
                <InfoItem label="Amount" value={money(order.refund.amount)} />
                <InfoItem label="Refund ID" value={order.refund.refundId} />
                <InfoItem label="Refundable Amount" value={money(order.refund.refundableAmount)} />
                <InfoItem label="Remark" value={order.refund.remark} />
              </div>
            ) : (
              <div className="odv2-empty-state">No refund on this order.</div>
            )}
            {order.amendment && (
              <>
                <hr />
                <div className="odv2-info-grid">
                  <InfoItem label="Amendment ID" value={order.amendment.amendmentId} />
                  <InfoItem label="Amendment Status" value={order.amendment.amendmentStatus} />
                </div>
              </>
            )}
          </Card>

          <Card icon={<BsGlobe />} title="Cancellation & Date Change Policy">
            {(order.itinerary ?? []).map((sector) => (
              <div className="odv2-sector" key={sector.sectorIndex}>
                <div className="odv2-sector-label">Segment {sector.sectorIndex + 1}</div>
                <PolicyBlock title="Cancellation Policy" policy={sector.fareRules?.cancellationPolicy} />
                <PolicyBlock title="Date Change Policy" policy={sector.fareRules?.dateChangePolicy} />
                <PolicyBlock title="No-show Policy" policy={sector.fareRules?.noShowPolicy} />
                <PolicyBlock title="Seat Chargeable" policy={sector.fareRules?.seatChargeablePolicy} />
              </div>
            ))}
          </Card>
        </>
      )}

      {activeTab === "timeline" && (
        <Card icon={<BsClockHistory />} title="Timeline">
          {order.timeline?.length ? (
            <div className="odv2-timeline">
              {order.timeline.map((event, idx) => (
                <div className="odv2-timeline-item" key={idx}>
                  <div className="odv2-timeline-label">
                    {renderValue(event.label)} {event.status && <Badge variant={bookingStatusVariant(event.status)}>{event.status}</Badge>}
                  </div>
                  <div className="odv2-timeline-time">{dateTime(event.timestamp)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="odv2-empty-state">No timeline events recorded.</div>
          )}
        </Card>
      )}
    </div>
  );
};

export default OrderDetailsV2;
